import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/AliasesService';
import { useAppSelector } from '../../../hooks/useTypedReactRedux';
import { selectionSelector } from '../selectors';
import { AliasFilters } from '../buttons/FilterRecordsButton';

export const useAliases = (externalFilters?: AliasFilters) => {
    const queryClient = useQueryClient();
    const selection = useAppSelector((state) => state.aliases ? selectionSelector(state) : {});

    const activeStatusFilter = externalFilters?.aliasStatus ?? null;
    const activeTypeFilter = externalFilters?.aliasType ?? null;

    const aliasesQuery = useInfiniteQuery({
        queryKey: ['aliases', activeStatusFilter, activeTypeFilter],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecords({ limit: 10, nextToken: pageParam, statusFilter: activeStatusFilter, typeFilter: activeTypeFilter }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: { id: string, name: string }[]) => {
            const promises = ids.map(item => deleteRecord(item.id, item.name));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
        }
    });

    const fetchRecordsAction = () => {
        if (aliasesQuery.hasNextPage) {
            aliasesQuery.fetchNextPage();
        }
    };

    const refetchRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['aliases'] });
    };

    const deleteRecords = () => {
        const selectedIds = Object.keys(selection).map(key => ({
            id: selection[key].id,
            name: selection[key].name
        }));
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const allItems = aliasesQuery.data?.pages?.flatMap(page => 
        (page && 'items' in page) ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        visibleCount: allItems.length,
        loading: aliasesQuery.isLoading,
        nextToken: aliasesQuery.hasNextPage ? 'has-more' : null,
        isFetchingNextPage: aliasesQuery.isFetchingNextPage,
        fetchRecords: fetchRecordsAction,
        refetchRecords,
        deleteRecords,
    };
};
